import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { AppAlert, AppButton, AppSpinner } from '../../ui';

const formatMoney = (value) => {
  if (value === null || value === undefined) return '— €';
  return `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
};

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 2) / 2;
  return `${rounded.toLocaleString('fr-FR')} h`;
};

const friendlyPhaseTitle = (title) => String(title ?? '')
  .replace(/^Phase\s+\d+\s*:\s*/i, '')
  .trim();

const ChiffrageInvestissementPreview = ({
  result,
  affichagePrix = true,
  onAffichagePrixChange,
  loading = false,
  onCopyDolibarr,
  onGenerateSlides,
  onPushDolibarr,
  onReset,
  canGenerateSlides = false,
  canPushDolibarr = false,
  generating = false,
  pushing = false,
  busy = false,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const collaboration = result?.collaboration;
  const blocked = collaboration?.required === true;
  const subs = result?.subscriptions ?? {};
  const annualLines = subs.annual_lines ?? [];
  const oneShotLines = subs.one_shot_lines ?? [];
  const phases = result?.phases ?? [];
  const hidePrices = !affichagePrix;

  const copyDetails = async () => {
    if (!onCopyDolibarr) return;
    await onCopyDolibarr();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  let body;
  if (loading && !result) {
    body = (
      <div className="chiffrage-preview-empty">
        <AppSpinner size="sm" centered={false} />
        <p>Calcul du devis en cours…</p>
      </div>
    );
  } else if (!result) {
    body = (
      <div className="chiffrage-preview-empty">
        <p>Répondez aux questions : le devis se calcule à chaque réponse.</p>
      </div>
    );
  } else if (blocked) {
    body = (
      <div className="chiffrage-collab">
        <AppAlert variant="warning">
          <strong>Cadrage avec l’équipe. </strong>
          {collaboration.intro}
        </AppAlert>
        <ul className="chiffrage-collab__list">
          {(collaboration.reasons ?? []).map((reason) => (
            <li key={reason.ref}>
              <strong>{reason.label}</strong>
              <span>{reason.message}</span>
            </li>
          ))}
        </ul>
        <p className="chiffrage-collab__hint">
          Le chiffrage, la présentation et l’envoi Dolibarr restent disponibles dès que vous revenez à une offre du catalogue.
        </p>
      </div>
    );
  } else {
    body = (
      <>
        <div className="chiffrage-hero">
          <span className="chiffrage-hero__label">Création du site · à payer une fois</span>
          <strong className="chiffrage-hero__amount">{formatMoney(result.total_price)}</strong>
          <span className="chiffrage-hero__meta">
            soit environ {Math.round(Number(result.total_hours) || 0)} h de réalisation
          </span>
        </div>
        <div className="chiffrage-hero__split">
          <div>
            <span>Total 1re année</span>
            <strong>{formatMoney(result.budget_n1)}</strong>
            <small>création + abonnements</small>
          </div>
          <div>
            <span>Les années suivantes</span>
            <strong>
              {formatMoney(result.budget_n2)}
              <small> / an</small>
            </strong>
            <small>abonnements seuls</small>
          </div>
        </div>
        <div className="chiffrage-lines">
          <span className="chiffrage-kicker">Chaque année</span>
          {annualLines.length === 0 ? (
            <p className="chiffrage-lines__empty">Aucun abonnement pour l’instant.</p>
          ) : annualLines.map((line) => (
            <div key={`${line.ref}-${line.qty}`} className="chiffrage-lines__row">
              <span>
                <strong>{line.qty > 1 ? `${line.label} × ${line.qty}` : line.label}</strong>
                <small>{line.payment === 'annuel' ? 'Abonnement' : ''}</small>
              </span>
              <b>{formatMoney(line.price)}</b>
            </div>
          ))}
          {oneShotLines.length > 0 ? (
            <>
              <span className="chiffrage-kicker">Une seule fois</span>
              {oneShotLines.map((line) => (
                <div key={line.ref} className="chiffrage-lines__row">
                  <span>
                    <strong>{line.label}</strong>
                    <small>Crédit temps, valable 1 an</small>
                  </span>
                  <b>{formatMoney(line.price)}</b>
                </div>
              ))}
            </>
          ) : null}
        </div>
        <button
          type="button"
          className="chiffrage-included"
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          <span>
            <strong>Ce qui est inclus</strong>
            <small>{phases.length} étape{phases.length > 1 ? 's' : ''} de réalisation</small>
          </span>
          <Icon icon={detailsOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'} width={18} />
        </button>
        {detailsOpen ? (
          <div className="chiffrage-included__body">
            <div className="chiffrage-included__bar">
              <span>Détail à coller dans Dolibarr si besoin.</span>
              {onCopyDolibarr ? (
                <AppButton variant="outline-secondary" className="chiffrage-copy" icon={copied ? 'lucide:check' : 'lucide:copy'} onClick={copyDetails}>
                  {copied ? 'Copié' : 'Copier le texte'}
                </AppButton>
              ) : null}
            </div>
            {phases.map((phase, index) => (
              <div key={`${phase.title}-${index}`} className="chiffrage-phase">
                <span className="chiffrage-phase__num">{index + 1}</span>
                <span className="chiffrage-phase__name">{friendlyPhaseTitle(phase.title)}</span>
                <span className="chiffrage-phase__meta">
                  {phase.hours != null ? formatHours(phase.hours) : ''}
                  {!hidePrices && phase.price != null ? ` · ${formatMoney(phase.price)}` : ''}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <aside className="chiffrage-recap">
      <div className="chiffrage-recap__card">
        <div className="chiffrage-recap__head">
          <span>Récapitulatif du devis</span>
          <button
            type="button"
            className="chiffrage-toggle"
            aria-pressed={affichagePrix}
            onClick={() => onAffichagePrixChange?.(!affichagePrix)}
          >
            <span className={`chiffrage-toggle__track${affichagePrix ? ' is-on' : ''}`}>
              <span className="chiffrage-toggle__knob" />
            </span>
            Afficher les prix
          </button>
        </div>
        {body}
      </div>
      <div className="chiffrage-recap__actions">
        <span>Quand le devis vous convient :</span>
        <AppButton
          variant="secondary"
          className="chiffrage-nav chiffrage-nav--dark"
          icon="lucide:presentation"
          loading={generating}
          disabled={blocked || !result || busy || !canGenerateSlides}
          onClick={onGenerateSlides}
        >
          Créer la présentation Google Slides
        </AppButton>
        <AppButton
          variant="outline-secondary"
          icon="lucide:send"
          loading={pushing}
          disabled={blocked || !result || busy || !canPushDolibarr}
          onClick={onPushDolibarr}
        >
          Envoyer le devis dans Dolibarr
        </AppButton>
        <AppButton
          variant="link"
          className="chiffrage-reset"
          icon="lucide:rotate-ccw"
          disabled={busy}
          onClick={onReset}
        >
          Tout réinitialiser
        </AppButton>
      </div>
    </aside>
  );
};

export default ChiffrageInvestissementPreview;
